import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { TextField, Button, Box, Stack } from '@mui/material'
import type { Tag } from '@/api/tagsApi'

const hexColorRegex = /^#[0-9A-Fa-f]{6}$/

const createTagSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t('tags.validation.nameRequired'))
      .max(255, t('tags.validation.nameMaxLength')),
    color: z
      .string()
      .optional()
      .refine(
        (val) => !val || val === '' || hexColorRegex.test(val),
        t('tags.validation.colorHex')
      )
      .transform((val) => (val && val.trim() ? val : undefined)),
  })

export type TagFormData = { name: string; color?: string }

interface TagFormProps {
  initialValues?: Tag
  onSubmit: (data: TagFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function TagForm({
  initialValues,
  onSubmit,
  onCancel,
  isLoading,
}: TagFormProps) {
  const { t } = useTranslation()
  const isEdit = !!initialValues

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TagFormData>({
    resolver: zodResolver(createTagSchema(t)),
    defaultValues: initialValues
      ? {
          name: initialValues.name,
          color: initialValues.color ?? '#6c757d',
        }
      : { name: '', color: '#6c757d' },
  })

  const handleFormSubmit = async (data: TagFormData) => {
    await onSubmit(data)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('tags.form.name')}
              placeholder={t('tags.form.namePlaceholder')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
              size="small"
            />
          )}
        />

        <Controller
          name="color"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('tags.form.color')}
              placeholder={t('tags.form.colorPlaceholder')}
              error={!!errors.color}
              helperText={errors.color?.message}
              fullWidth
              size="small"
              type="color"
              InputProps={{
                sx: { height: 40, minHeight: 40, cursor: 'pointer' },
              }}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading} size="small">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            size="small"
          >
            {isEdit ? t('common.save') : t('tags.create')}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}
